import { useId, useRef, useState, type ReactNode } from "react";

/** Label, help text, live character counter and the error for one field. */
export function Field(props: { label: string; required?: boolean; help?: string; error?: string; count?: { value: number; max: number }; htmlFor?: string; children: ReactNode; className?: string }) {
  const over = props.count && props.count.value > props.count.max;
  return (
    <div className={`field ${props.error ? "has-error" : ""} ${props.className ?? ""}`}>
      <div className="field-head">
        <label htmlFor={props.htmlFor}>
          {props.label}
          {props.required && (
            <span className="req" aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </label>
        {props.count && (
          <span className={`counter ${over ? "over" : ""}`} aria-live="polite">
            {props.count.value}/{props.count.max}
          </span>
        )}
      </div>
      {props.help && <p className="help" id={props.htmlFor ? `${props.htmlFor}-help` : undefined}>{props.help}</p>}
      {props.children}
      {props.error && (
        <p className="error" role="alert" id={props.htmlFor ? `${props.htmlFor}-err` : undefined}>
          {props.error}
        </p>
      )}
    </div>
  );
}

export function describedBy(id: string, hasHelp: boolean, hasError: boolean) {
  const ids = [hasHelp && `${id}-help`, hasError && `${id}-err`].filter(Boolean).join(" ");
  return { "aria-describedby": ids || undefined, "aria-invalid": hasError || undefined };
}

export function TextInput(props: { label: string; value: string; onChange: (v: string) => void; max?: number; required?: boolean; help?: string; error?: string; placeholder?: string; type?: string; multiline?: boolean; rows?: number; id?: string; autoComplete?: string }) {
  const auto = useId();
  const id = props.id ?? auto;
  const common = {
    id,
    value: props.value,
    placeholder: props.placeholder,
    onChange: (e: { target: { value: string } }) => props.onChange(e.target.value),
    ...describedBy(id, !!props.help, !!props.error),
  };
  return (
    <Field label={props.label} required={props.required} help={props.help} error={props.error} htmlFor={id} count={props.max ? { value: props.value.length, max: props.max } : undefined}>
      {props.multiline ? <textarea {...common} rows={props.rows ?? 4} /> : <input {...common} type={props.type ?? "text"} autoComplete={props.autoComplete} />}
    </Field>
  );
}

export function Toggle(props: { label: string; checked: boolean; onChange: (v: boolean) => void; help?: string }) {
  const id = useId();
  return (
    <div className="toggle-row">
      <div>
        <label htmlFor={id}>{props.label}</label>
        {props.help && <p className="help">{props.help}</p>}
      </div>
      <input id={id} type="checkbox" role="switch" checked={props.checked} onChange={(e) => props.onChange(e.target.checked)} />
    </div>
  );
}

export function Slider(props: { label: string; value: number; onChange: (v: number) => void; low: string; high: string; help: string; id: string }) {
  return (
    <Field label={props.label} help={props.help} htmlFor={props.id}>
      <div className="slider">
        <input id={props.id} type="range" min={0} max={100} step={10} value={props.value} onChange={(e) => props.onChange(Number(e.target.value))} aria-valuetext={`${props.value} of 100`} aria-describedby={`${props.id}-help`} />
        <output className="slider-value">{props.value}</output>
      </div>
      <div className="slider-ends" aria-hidden="true">
        <span>{props.low}</span>
        <span>{props.high}</span>
      </div>
    </Field>
  );
}

/** A list the person adds to (starters, emails, domains) with a counter and per-row errors. */
export function ListEditor(props: {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  max?: number;
  maxLength?: number;
  required?: boolean;
  help?: string;
  placeholder?: string;
  addLabel: string;
  errors?: Record<string, string>;
  errorPrefix: string;
  type?: string;
  multiline?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const id = useId();
  const addButton = useRef<HTMLButtonElement>(null);
  const full = props.max !== undefined && props.items.length >= props.max;
  const add = () => {
    const v = draft.trim();
    if (!v || full) return;
    props.onChange([...props.items, v]);
    setDraft("");
  };
  const pending = draft.trim() !== "" && !full;
  const listError = props.errors?.[props.errorPrefix];
  return (
    <Field label={props.label} required={props.required} help={props.help} error={listError} htmlFor={id}>
      {props.max !== undefined && (
        <span className="chip-count" aria-live="polite">
          {props.items.length}/{props.max}
        </span>
      )}
      <ul className="rows">
        {props.items.map((it, i) => {
          const err = props.errors?.[`${props.errorPrefix}.${i}`];
          return (
            <li key={i}>
              <div className="row">
                <input
                  aria-label={`${props.label} ${i + 1}`}
                  value={it}
                  type={props.type ?? "text"}
                  onChange={(e) => props.onChange(props.items.map((x, j) => (j === i ? e.target.value : x)))}
                  aria-invalid={!!err || undefined}
                />
                <button type="button" className="btn ghost" onClick={() => props.onChange(props.items.filter((_, j) => j !== i))} aria-label={`Remove ${props.label} ${i + 1}`}>
                  Remove
                </button>
              </div>
              {props.maxLength && <span className={`counter ${it.length > props.maxLength ? "over" : ""}`}>{it.length}/{props.maxLength}</span>}
              {err && <p className="error" role="alert">{err}</p>}
            </li>
          );
        })}
      </ul>
      <div className="row">
        <input
          id={id}
          value={draft}
          type={props.type ?? "text"}
          placeholder={props.placeholder}
          disabled={full}
          onChange={(e) => setDraft(e.target.value)}
          // Text left in the box is added when the person moves on, so it is never silently lost.
          onBlur={(e) => {
            if (e.relatedTarget !== addButton.current) add(); // going to the Add button: let its click do it, once
          }}
          aria-describedby={pending ? `${id}-pending` : undefined}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button ref={addButton} type="button" className="btn secondary" onClick={add} disabled={full || !draft.trim()}>
          {props.addLabel}
        </button>
      </div>
      {pending && (
        <p className="hint" id={`${id}-pending`}>
          Not added yet. Press “{props.addLabel}” or Enter, or click elsewhere, and it will be added.
        </p>
      )}
    </Field>
  );
}

export function Section(props: { title: string; children: ReactNode; help?: string }) {
  return (
    <section className="card">
      <h3>{props.title}</h3>
      {props.help && <p className="help">{props.help}</p>}
      {props.children}
    </section>
  );
}

export function Badge(props: { tone: "ok" | "warn" | "bad" | "info" | "muted"; children: ReactNode }) {
  return <span className={`badge ${props.tone}`}>{props.children}</span>;
}
